import {Component, Element, Event, EventEmitter, h, Host, Prop, State, Watch} from "@stencil/core"
import {GovHost} from "../../helpers/Dom/template.types";
import {govHost} from "../../helpers/Dom/template";
import {ATTRIBUTE_THEME_NAME, ThemeSwitchClass, ThemeSwitchSizes, ThemeSwitchSizesType} from "./constants";
import {isWindow} from "../../helpers/Dom/win";
import {ThemeList, ThemeSwitchEvent} from "./gov-theme-switch.types";
import {validateProp} from "../../helpers/Validate/props";
import {getCookie, setCookie} from "../../helpers/Dom/document";

@Component({
	tag: "gov-theme-switch",
})
export class GovThemeSwitch {
	private h: GovHost

	constructor() {
		this.h = govHost(this.host)
	}

	@Element() host: HTMLGovThemeSwitchElement

	/**
	 * Switch's size.
	 */
	@Prop() readonly size?: ThemeSwitchSizesType = "m"
	/**
	 * Display switch label
	 */
	@Prop({attribute: "display-label"}) readonly displayLabel? = false
	/**
	 * Label in light mode
	 */
	@Prop({attribute: "aria-label-light"}) readonly ariaLabelLight? = "Přepnout na tmavý mód"
	/**
	 * Label in dark mode
	 */
	@Prop({attribute: "aria-label-dark"}) readonly ariaLabelDark? = "Přepnout na světlý mód"
	/**
	 * Label in light mode
	 */
	@Prop({attribute: "label-light"}) readonly labelLight? =  "Světlý mód"
	/**
	 * Label in dark mode
	 */
	@Prop({attribute: "label-dark"}) readonly labelDark? = "Tmavý mód"
	/**
	 * Called when the theme is changed by the user
	 */
	@Event({eventName: "gov-change"}) govChange: EventEmitter<ThemeSwitchEvent>

	@Watch("size")
	validateSize(newValue: string): void {
		validateProp(ThemeSwitchSizes, newValue, ThemeSwitchClass.root)
	}

	@Watch("theme")
	watchTheme(newValue: ThemeList): void {
		this.htmlElement.setAttribute(ATTRIBUTE_THEME_NAME, newValue);
		this.htmlElement.classList.remove(...Object.keys(ThemeList));
		this.htmlElement.classList.add(newValue);
		setCookie(ATTRIBUTE_THEME_NAME, newValue);
	}

	@State() theme: ThemeList = ThemeList.auto

	private get storageValue() {
		return getCookie(ATTRIBUTE_THEME_NAME)
	}

	private get htmlElement(): HTMLElement {
		return document.documentElement
	}

	private get isSystemPrefersColorDark(): boolean {
		return isWindow && window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
	}

	componentWillLoad() {
		this.initDefaultState()
		this.validateSize(this.size)
	}

	private initDefaultState() {
		const options = Object.keys(ThemeList);
		let currentState = this.storageValue;
		const htmlState = this.htmlElement.getAttribute(ATTRIBUTE_THEME_NAME)

		if (currentState && options.includes(currentState)) {
			this.theme = currentState as ThemeList;
		} else if (htmlState && options.includes(htmlState)) {
			this.theme = htmlState as ThemeList;
		} else {
			this.theme = ThemeList.auto;
		}
	}

	private setThemeState = (state: ThemeList) => {
		this.theme = state;

		this.govChange.emit({
			component: ThemeSwitchClass.root,
			state: this.theme,
			ref: this.host
		})
	}

	render() {
		let isDark = this.theme === ThemeList.dark;
		if (this.theme === ThemeList.auto) {
			isDark = this.isSystemPrefersColorDark;
		}
		return (
			<Host class={this.h.classes(ThemeSwitchClass.root)}
				  theme={isDark ? ThemeList.dark : ThemeList.light}
				  size={this.size}>
				<button onClick={() => this.setThemeState(isDark ? ThemeList.light : ThemeList.dark)}
						aria-current={this.theme}
						aria-label={isDark ? this.ariaLabelDark : this.ariaLabelLight}>
					<span>
						<span class={ThemeSwitchClass.indicator}>
							{ThemeList.light && <gov-icon name="sun" class={ThemeSwitchClass.iconLight} type="components"></gov-icon>}
							{ThemeList.dark && <gov-icon name="moon" class={ThemeSwitchClass.iconDark} type="components"></gov-icon>}
						</span>
						{this.displayLabel ? (
							<span class={ThemeSwitchClass.label}>
								{isDark ? this.labelDark : this.labelLight}
							</span>
							) : null
						}
					</span>
				</button>
			</Host>
		)
	}
}
